import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Landmark, LayoutGrid, ArrowLeftRight, ScrollText, LogOut } from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/transfer", label: "Transfer", icon: ArrowLeftRight, testid: "nav-transfer" },
  { to: "/transactions", label: "Ledger", icon: ScrollText, testid: "nav-transactions" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const onLogout = () => {
    logout();
    nav("/auth");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="header-logo">
            <Landmark className="w-6 h-6 text-institutional-gold" strokeWidth={1.5} />
            <div className="leading-tight">
              <div className="uppercase tracking-[0.25em] text-[10px] text-slate-500 font-medium">
                Federal Reserve
              </div>
              <div className="font-serif text-lg text-federal-navy -mt-0.5">
                Bank of Custody
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 text-sm uppercase tracking-wider transition-colors ${
                    isActive
                      ? "text-federal-navy border-b-2 border-institutional-gold"
                      : "text-slate-500 hover:text-federal-navy border-b-2 border-transparent"
                  }`
                }
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-xs uppercase tracking-widest text-slate-500">
                Client
              </div>
              <div className="text-sm font-medium text-federal-navy" data-testid="header-username">
                {user?.full_name || user?.username}
              </div>
            </div>
            <Button
              onClick={onLogout}
              data-testid="logout-btn"
              variant="outline"
              className="rounded-none border-slate-300 hover:bg-federal-navy hover:text-white hover:border-federal-navy uppercase tracking-wider text-xs"
            >
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Sign out
            </Button>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center gap-1 py-3 text-[10px] uppercase tracking-widest ${
                    isActive ? "text-federal-navy" : "text-slate-500"
                  }`
                }
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {children}
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-widest">Federal Reserve — Institutional Custody</span>
          </div>
          <div className="italic">Member custodian · Simulated environment</div>
        </div>
      </footer>
    </div>
  );
}
